import { Button, Card, Flex, InputNumber, Switch, Typography } from 'antd'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { useCompanyUsage, useUpdateCompanyOverageSettings } from '../../company/hooks'
import { formatPrice } from '../pricing'

/**
 * Candidates past the plan's allowance, billed per candidate. The Website plan includes none, so there every candidate is
 * paid for, and it always has a maximum.
 */
export function OverageSettingsCard() {
    const { activeCompany, websitePlan } = useAuth()
    const { t } = useTranslation()

    const companyId = activeCompany?.id
    const { data: usage } = useCompanyUsage(companyId ?? '')
    const { mutate, isPending } = useUpdateCompanyOverageSettings(companyId)

    const [enabled, setEnabled] = useState(activeCompany?.overagesEnabled ?? false)
    const [limit, setLimit] = useState<number | null>(activeCompany?.overageLimit ?? null)

    if (!activeCompany) {
        return null
    }

    const unitAmount = usage?.overageUnitAmount ?? 0
    const formattedUnitPrice = formatPrice(unitAmount, 'EUR')

    const handleSave = () => {
        mutate({ overagesEnabled: enabled, overageLimit: enabled ? limit : null })
    }

    return (
        <Card className="w-full">
            <Flex
                vertical
                gap="middle"
            >
                <div>
                    <Typography.Text type="secondary">{t('Pay-as-you-go')}</Typography.Text>
                    <Typography.Title
                        level={4}
                        className="mt-2 mb-0"
                    >
                        {websitePlan ? t('Candidates via WhatsApp') : t('Extra conversations')}
                    </Typography.Title>
                </div>

                <Flex
                    justify="space-between"
                    align="center"
                    gap="middle"
                >
                    <Typography.Text type="secondary">
                        {websitePlan
                            ? t('Let candidates apply via WhatsApp and bill me {{price}} per candidate', {
                                  price: formattedUnitPrice,
                              })
                            : t('Allow going over my plan and bill me {{price}} per extra conversation', {
                                  price: formattedUnitPrice,
                              })}
                    </Typography.Text>
                    <Switch
                        checked={enabled}
                        onChange={setEnabled}
                    />
                </Flex>

                {enabled && (
                    <Flex
                        vertical
                        gap="small"
                    >
                        <Typography.Text type="secondary">
                            {websitePlan
                                ? t('Maximum candidates per month')
                                : t('Maximum extra conversations per month')}
                        </Typography.Text>
                        <InputNumber
                            min={1}
                            className="w-full"
                            value={limit}
                            onChange={setLimit}
                            placeholder={websitePlan ? undefined : t('No limit')}
                        />
                        {websitePlan && limit !== null && (
                            <Typography.Text type="secondary">
                                {t('At most {{amount}} per month, excl. VAT.', {
                                    amount: formatPrice(limit * unitAmount, 'EUR'),
                                })}
                            </Typography.Text>
                        )}
                    </Flex>
                )}

                <Flex justify="flex-end">
                    <Button
                        type="primary"
                        loading={isPending}
                        // The Website plan pays for every candidate: never without a maximum.
                        disabled={websitePlan && enabled && limit === null}
                        onClick={handleSave}
                    >
                        {t('Save')}
                    </Button>
                </Flex>
            </Flex>
        </Card>
    )
}
